import { FC, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { ChevronDownIcon } from './ExportIcons'
import {
  formatMonthLabel,
  isBeforeMinExportMonth,
  MIN_EXPORT_YEAR,
  MONTH_NAMES,
  parseExportYyyymm,
  toYyyymm,
} from '../utils/exportMonth'

interface MonthPickerProps {
  value: string
  onChange: (yyyymm: string) => void
}

export const MonthPicker: FC<MonthPickerProps> = ({ value, onChange }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const today = useMemo(() => new Date(), [])
  const parsedValue = useMemo(() => {
    const parsed = parseExportYyyymm(value)

    if (!parsed) {
      return { year: today.getFullYear(), month: today.getMonth() }
    }

    return { year: parsed.year, month: parsed.month - 1 }
  }, [today, value])

  const [viewYear, setViewYear] = useState(parsedValue.year)
  const minYear = MIN_EXPORT_YEAR
  const maxYear = today.getFullYear()

  const handleOpen = () => {
    setViewYear(parsedValue.year)
    onOpen()
  }

  const handleSelectMonth = (monthIndex: number) => {
    onChange(toYyyymm(viewYear, monthIndex))
    onClose()
  }

  const isFutureMonth = (monthIndex: number) =>
    viewYear === today.getFullYear() && monthIndex > today.getMonth()

  const isMonthDisabled = (monthIndex: number) =>
    isFutureMonth(monthIndex) || isBeforeMinExportMonth(viewYear, monthIndex)

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Button
          size="sm"
          variant="outline"
          minW="140px"
          h="34px"
          px={3}
          fontSize="12px"
          fontWeight="medium"
          color="gray.600"
          borderColor="gray.200"
          bg="white"
          onClick={handleOpen}
          _hover={{ borderColor: 'gray.300' }}
          _active={{ borderColor: 'blue.500', boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.15)' }}
          aria-label="Select month"
        >
          <Flex as="span" w="100%" align="center" justify="space-between" gap={2}>
            {formatMonthLabel(parsedValue.year, parsedValue.month)}
            <ChevronDownIcon />
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent w="240px" borderRadius="12px" boxShadow="0 8px 24px rgba(15, 23, 42, 0.12)">
        <PopoverBody p={3.5}>
          <HStack justify="space-between" mb={3}>
            <Box
              as="button"
              type="button"
              aria-label="Previous year"
              w="26px"
              h="26px"
              borderRadius="6px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.600"
              transition="background 0.12s"
              _hover={{ bg: 'gray.100' }}
              _disabled={{ color: 'gray.300', cursor: 'not-allowed' }}
              disabled={viewYear <= minYear}
              onClick={() => setViewYear((year) => year - 1)}
            >
              <Icon as={FiChevronLeft} boxSize="12px" aria-hidden />
            </Box>
            <Text fontSize="13px" fontWeight="semibold" color="gray.800">
              {viewYear}
            </Text>
            <Box
              as="button"
              type="button"
              aria-label="Next year"
              w="26px"
              h="26px"
              borderRadius="6px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.600"
              transition="background 0.12s"
              _hover={{ bg: 'gray.100' }}
              _disabled={{ color: 'gray.300', cursor: 'not-allowed' }}
              disabled={viewYear >= maxYear}
              onClick={() => setViewYear((year) => year + 1)}
            >
              <Icon as={FiChevronRight} boxSize="12px" aria-hidden />
            </Box>
          </HStack>
          <Grid templateColumns="repeat(3, 1fr)" gap={1.5}>
            {MONTH_NAMES.map((monthLabel, monthIndex) => {
              const isSelected = viewYear === parsedValue.year && monthIndex === parsedValue.month
              const isDisabled = isMonthDisabled(monthIndex)

              return (
                <Button
                  key={monthLabel}
                  size="sm"
                  h="32px"
                  fontSize="12px"
                  fontWeight={isSelected ? 'semibold' : 'medium'}
                  variant={isSelected ? 'solid' : 'ghost'}
                  colorScheme={isSelected ? 'blue' : 'gray'}
                  isDisabled={isDisabled}
                  onClick={() => handleSelectMonth(monthIndex)}
                >
                  {monthLabel}
                </Button>
              )
            })}
          </Grid>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
